import { flattenDeep, union } from 'lodash';
import moment from 'moment';

import { getNotificationPriorityOrDefault } from '~/components/Notifications/utils';
import { getDefaultReminderTarget } from '~/components/ReminderNotificationContainer';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { UserModel } from '~/components/types/user-types';
import { isoDateToUs } from '~/DateTimeUtils';
import type { ContactFullModel, PhoneCallCommunication } from '~/server_shared/swagger/data-contracts';
import { capitalize } from '~/Utils';

import type { CommunicationType, DocumentMetadata } from '../types';

import type { ChannelType, CommunicationIconSettings } from './constants';
import { channelTypeToErrorIconSettings, channelTypeToIconSettings } from './constants';
import type { PartialReminder } from './types';

export const calcExposureIds = (
  claim: ClaimPolymorph | undefined,
  user: { id: string },
  contact: ContactFullModel | undefined,
  attachedDocuments: DocumentMetadata[] = []
): number[] => {
  if (!claim) {
    return [];
  }
  const userRelatedExposureIds = claim.exposures
    .filter((exposure) => exposure.handling_adjuster_id && exposure.handling_adjuster_id === user.id)
    .map((exposure) => exposure.id);
  const contactExposureIds = contact ? contact.exposure_ids : [];
  const attachedDocumentsExposureIds = attachedDocuments
    ? flattenDeep(attachedDocuments.map((document: { exposure_ids: number[] }) => document.exposure_ids))
    : [];
  const exposureIdsCombined = union(userRelatedExposureIds, contactExposureIds, attachedDocumentsExposureIds).filter(
    (id): id is number => id !== undefined
  );
  exposureIdsCombined.sort();
  return exposureIdsCombined;
};

export const getCommunicationReminderDefaultValues = (
  channel: ChannelType,
  contact: ContactFullModel | undefined,
  user: UserModel,
  claim: ClaimPolymorph | undefined
): PartialReminder => {
  const channelDescription = capitalize(channel.replace('_', ' '));
  return {
    title: 'Follow up on Communication',
    details: `${channelDescription} was sent on ${isoDateToUs(new Date())}${contact ? ' to ' + contact.full_name : ''}`,
    due_date: moment().add(30, 'd'),
    target_id: claim ? getDefaultReminderTarget(user, claim) : -1, // -1 = Self
    was_manually_edited: false,
    priority: getNotificationPriorityOrDefault(),
    category_id: null,
  };
};

export const getCommunicationIconSettings = (
  communication: CommunicationType,
  channel: ChannelType
): CommunicationIconSettings => {
  const isDeliveryError =
    ['email', 'sms'].includes(channel) &&
    communication &&
    'is_delivery_issue_error_exists' in communication &&
    communication.is_delivery_issue_error_exists;
  return isDeliveryError ? channelTypeToErrorIconSettings[channel] : channelTypeToIconSettings[channel];
};

export const formatCallDuration = (callDurationSec: number): string => {
  const seconds = callDurationSec % 60;
  const secString = seconds >= 10 ? String(seconds) : `0${seconds}`;
  return `${Math.floor(callDurationSec / 60)}:${secString}`;
};

export const getPhoneCallCommunicationSpecificIdentifier = (communication: PhoneCallCommunication) => {
  return communication?.specific_identifier || '';
};

export const phoneCallCommunicationToFormikValues = (communication: PhoneCallCommunication) => ({
  summary: communication.summary,
  content: communication.content,
  is_no_answer: communication.is_no_answer,
});
